import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import LabelDragAndDropQuizPreview from "../../features/previews/label_drag_and_drop_quiz/LabelDragAndDropQuizPreview";
import { GetMigratedProperties } from "../../features/project_migration/InteractionMigrationHelper";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor } from "../Actor";
import { requiredGlobalQuizProperties } from "../GlobalQuizProperty";
import {
  InteractionProperty,
  InteractionPropertyType,
} from "../InteractionProperty";
import { Prop } from "../Prop";
import { IAnswer } from "../Quiz";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";

export interface ILabelDragAndDropQuizLabel {
  id: string;
  text: string;
}

export interface ILabelDragAndDropQuizDropZone {
  id: string;
  correctLabelId?: string;
  xPos: number;
  yPos: number;
  xSize: number;
  ySize: number;
}

export interface ILabelDragAndDropQuiz extends Interaction {
  title: string;
  imageAssetId?: string;
  labels: ILabelDragAndDropQuizLabel[];
  dropZones: ILabelDragAndDropQuizDropZone[];
}

export const LABEL_DRAG_AND_DROP_QUIZ_MAX_LABELS = 5;

const requiredLabelDragAndDropQuizProperties: InteractionProperty[] = [
  ...requiredGlobalQuizProperties,
  {
    id: "textColor",
    name: "Text Color",
    type: InteractionPropertyType.color,
    value: { r: 255, g: 255, b: 255, a: 1 },
  },
];

const createDropZone = (
  correctLabelId: string | undefined,
  xPos: number,
  yPos: number,
): ILabelDragAndDropQuizDropZone => ({
  id: GetNewGuid(),
  correctLabelId,
  xPos,
  yPos,
  xSize: 0.18,
  ySize: 0.14,
});

const migrateDropZoneGeometry = (
  dropZone: ILabelDragAndDropQuizDropZone & { text?: string },
  index: number,
): ILabelDragAndDropQuizDropZone => {
  const { text: _legacyText, ...rest } = dropZone;
  return {
    ...rest,
    xPos: dropZone.xPos ?? (index % 2 === 0 ? 0.4 : 0.6),
    yPos: dropZone.yPos ?? 0.5,
    xSize: dropZone.xSize ?? 0.18,
    ySize: dropZone.ySize ?? 0.14,
  };
};

export const labelDragAndDropQuizInteraction =
  (): InteractionFactoryEntity => {
    const component = <LabelDragAndDropQuizPreview />;

    const create = (
      getKeys: (amount: number) => string[],
    ): ILabelDragAndDropQuiz => {
      const keys = getKeys(3);
      const firstLabelId = GetNewGuid();
      const secondLabelId = GetNewGuid();

      return {
        id: GetNewGuid(),
        name: "Label Drag And Drop Quiz",
        type: InteractionType.LabelDragAndDropQuiz,
        title: keys[0],
        imageAssetId: "",
        labels: [
          { id: firstLabelId, text: keys[1] },
          { id: secondLabelId, text: keys[2] },
        ],
        dropZones: [
          createDropZone(firstLabelId, 0.4, 0.5),
          createDropZone(secondLabelId, 0.6, 0.5),
        ],
        properties: [...requiredLabelDragAndDropQuizProperties],
      };
    };

    const migrate = (
      from: number,
      to: number,
      interaction: Interaction,
    ): ILabelDragAndDropQuiz => {
      const casted = interaction as ILabelDragAndDropQuiz;

      if (casted.properties === undefined) {
        console.log(
          "[Label Drag And Drop Quiz] migrated " +
            requiredLabelDragAndDropQuizProperties.length +
            " properties",
        );
        return {
          ...casted,
          imageAssetId: casted.imageAssetId ?? "",
          dropZones: casted.dropZones.map(migrateDropZoneGeometry),
          properties: [...requiredLabelDragAndDropQuizProperties],
        };
      }

      const newProperties = GetMigratedProperties(
        requiredLabelDragAndDropQuizProperties,
        casted.properties,
      );

      console.log(
        "[Label Drag And Drop Quiz] migrated " +
          (newProperties.length - casted.properties.length) +
          " properties",
      );

      return {
        ...casted,
        imageAssetId: casted.imageAssetId ?? "",
        dropZones: casted.dropZones.map(migrateDropZoneGeometry),
        properties: newProperties,
      };
    };

    const copy = (
      interaction: Interaction,
      getKeys: (amount: number) => string[],
      replaceKey: (from: string, to: string) => void,
      getAnswerArray: (
        originalAnswers: IAnswer[],
        newKeys: string[],
      ) => IAnswer[],
    ): ILabelDragAndDropQuiz => {
      const quiz = interaction as ILabelDragAndDropQuiz;
      const newKeys = getKeys(1 + quiz.labels.length);
      const newTitleKey = newKeys[0];
      replaceKey(quiz.title, newTitleKey);

      const copiedLabels = quiz.labels.map((label, index) => {
        const text = newKeys[index + 1];
        replaceKey(label.text, text);

        return {
          id: GetNewGuid(),
          text,
          originalId: label.id,
        };
      });

      const copiedLabelIdByOriginalId = new Map(
        copiedLabels.map((label) => [label.originalId, label.id]),
      );

      const copiedDropZones = quiz.dropZones.map((dropZone) => ({
        id: GetNewGuid(),
        xPos: dropZone.xPos,
        yPos: dropZone.yPos,
        xSize: dropZone.xSize,
        ySize: dropZone.ySize,
        correctLabelId: dropZone.correctLabelId
          ? copiedLabelIdByOriginalId.get(dropZone.correctLabelId)
          : undefined,
      }));

      return {
        id: GetNewGuid(),
        name: interaction.name,
        type: InteractionType.LabelDragAndDropQuiz,
        title: newTitleKey,
        imageAssetId: quiz.imageAssetId ?? "",
        labels: copiedLabels.map(({ originalId, ...label }) => label),
        dropZones: copiedDropZones.map(migrateDropZoneGeometry),
        properties: quiz.properties
          ? GetMigratedProperties(
              requiredLabelDragAndDropQuizProperties,
              quiz.properties,
            )
          : [...requiredLabelDragAndDropQuizProperties],
      };
    };

    const validate = (
      interaction: Interaction,
      getValue: (key: string) => string,
      take: Take,
      actorPresets: Actor[],
    ): string[] => {
      const quiz = interaction as ILabelDragAndDropQuiz;
      const warnings: string[] = [];

      ValidationHelper.ValidateKey(quiz.title, getValue, warnings, "Title");
      ValidationHelper.ValidateAssetId(
        quiz.imageAssetId ?? "",
        warnings,
        "Image",
      );

      quiz.labels.forEach((label, index) => {
        ValidationHelper.ValidateKey(
          label.text,
          getValue,
          warnings,
          `Label ${index + 1}`,
        );
      });

      return warnings;
    };

    const filterType = "Label Drag And Drop Quiz";

    const filter = (
      interaction: Interaction,
      takeName: string,
      getMatchingResults: (
        searchableStrings: string[],
        takeName: string,
        interaction: Interaction,
        filterType: string,
      ) => SearchResultData[],
    ): SearchResultData[] => {
      const quiz = interaction as ILabelDragAndDropQuiz;
      const searchableStrings = [
        quiz.name,
        quiz.title,
        quiz.imageAssetId ?? "",
        ...quiz.labels.map((label) => label.text),
      ];

      return getMatchingResults(
        searchableStrings,
        takeName,
        interaction,
        filterType,
      );
    };

    const validateErrors = (
      interaction: Interaction,
      parentTake: Take,
      currentTake: Take,
      actorPresets: Actor[],
      allPropPresets: Prop[],
      localizedStringRecords: LocalizedStringRecord,
    ): string[] => {
      const quiz = interaction as ILabelDragAndDropQuiz;
      const errors: string[] = [];
      const labelIds = new Set(quiz.labels.map((label) => label.id));
      const usedLabelIds = new Set<string>();
      const duplicateLabelIds = new Set<string>();

      if (quiz.labels.length === 0) {
        errors.push("Quiz must have at least one label");
      }

      if (quiz.labels.length > LABEL_DRAG_AND_DROP_QUIZ_MAX_LABELS) {
        errors.push(
          `Quiz can have at most ${LABEL_DRAG_AND_DROP_QUIZ_MAX_LABELS} labels`,
        );
      }

      if (quiz.dropZones.length === 0) {
        errors.push("Quiz must have at least one drop zone");
      }

      quiz.dropZones.forEach((dropZone, index) => {
        if (!dropZone.correctLabelId) {
          return;
        }

        if (!labelIds.has(dropZone.correctLabelId)) {
          errors.push(`Drop zone ${index + 1} is mapped to a missing label`);
          return;
        }

        if (usedLabelIds.has(dropZone.correctLabelId)) {
          duplicateLabelIds.add(dropZone.correctLabelId);
          return;
        }

        usedLabelIds.add(dropZone.correctLabelId);
      });

      duplicateLabelIds.forEach((labelId) => {
        const labelIndex = quiz.labels.findIndex((label) => label.id === labelId);
        errors.push(`Label ${labelIndex + 1} is mapped to more than one drop zone`);
      });

      return errors;
    };

    return {
      View: component,
      FilterType: filterType,
      Create: create,
      Migrate: migrate,
      Copy: copy,
      Validate: validate,
      Filter: filter,
      ValidateErrors: validateErrors,
    };
  };
